import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { Asset, Collection } from "../../generated/gql/graphql";
import useCollectionAssets from "../../hooks/mutation/collection/use-collection-assets";
import useCreateCollection from "../../hooks/mutation/collection/use-create-collection";
import useCollections from "../../hooks/query/collection/use-collections";
import useText from "../../hooks/use-text";
import { parseType } from "../../utils/graphql";
import { LocalizedStrings, SupportedLanguages } from "../../utils/lang";
import MediaTypeIcon from "../media-format/media-type-icon";

type Props = {
  openModal: boolean;
  selectedAssets: Asset[];
  closeModal: Dispatch<SetStateAction<boolean>>;
};

export default function CollectionModal(props: Props): React.ReactElement {
  const { selectedAssets, openModal, closeModal } = props;

  const [isSubmittingCreateCollection, setIsSubmittingCreateCollection] =
    useState(false);
  const [isPending, setPending] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState<string>("");
  const [selectedCollection, setSelectedCollection] = useState<Collection[]>(
    []
  );
  const [collections, setCollections] = useState<Collection[]>([]);
  const { data } = useCollections();
  const { createCollection } = useCreateCollection();
  const { batchCreateCollectionAssets } = useCollectionAssets();

  const { strings } = useText(local);

  useEffect(() => {
    if (!data) {
      return;
    }

    setCollections(parseType<Collection[]>("collections", data));
  }, [data]);

  function onCheckChange(collection: Collection, checked: boolean): void {
    if (checked) {
      setSelectedCollection((prev) => prev.concat([collection]));
    } else {
      setSelectedCollection((prev) =>
        prev.filter((v) => v.id !== collection.id)
      );
    }
  }

  async function onCreateCollection(): Promise<void> {
    if (!newCollectionName || isSubmittingCreateCollection) {
      return;
    }

    try {
      setIsSubmittingCreateCollection(true);
      const result = (await createCollection({
        name: newCollectionName,
      })) as {
        data: {
          createCollection: Collection;
        };
      };

      if (!result || !result.data || !result.data.createCollection) {
        throw new Error(`Failed to create collection. ${result}`);
      }

      const newCollection = result?.data.createCollection;
      setCollections((prev) => [newCollection].concat(prev));
      setNewCollectionName("");
      enqueueSnackbar(strings.createCollection.notification.success, {
        variant: "success",
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(strings.createCollection.notification.failed, {
        variant: "error",
      });
    } finally {
      setIsSubmittingCreateCollection(false);
    }
  }

  async function onAddAssetsToCollections(): Promise<void> {
    try {
      setPending(true);
      const assetIds = selectedAssets.map((asset) => asset.id);
      await batchCreateCollectionAssets({
        items: selectedCollection.map((collection) => ({
          collectionId: collection.id,
          assetIds: assetIds,
        })),
      });

      closeModal(false);
      enqueueSnackbar(strings.notification.success, { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(strings.notification.failed, { variant: "error" });
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={openModal} onClose={() => closeModal(false)} maxWidth="md">
      <DialogContent className="relative flex h-full min-w-[650px] max-h-[500px] space-x-[20px]">
        <div className="w-1/2 h-full">
          <h2 className="text-[24px] text-[#DD2127]">{strings.title}</h2>
          <ul className="space-y-5 max-h-[425px] overflow-y-scroll scrollbar-w-0">
            {selectedAssets?.map((v) => (
              <li key={v.id} className="flex space-x-5 items-center">
                <div className="flex items-center justify-center w-50 h-50 border shrink-0">
                  <MediaTypeIcon asset={v} classes="object-contain" />
                </div>
                <p className="text-[14px] text-gray-500 max-w-md break-all uppercase">
                  {v.name}
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-1 w-1/2 flex-col justify-between h-auto">
          <div className="text-[14px]">{strings.createCollection.label}</div>
          <div className="flex mb-[10px] items-center">
            <TextField
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewCollectionName(e.target.value)
              }
              sx={{ input: { fontSize: "14px" } }}
              margin="dense"
              type="text"
              fullWidth
              variant="standard"
              placeholder={strings.createCollection.placeholder}
              value={newCollectionName}
            />
            <button
              style={{
                opacity: !newCollectionName.replace(/\s/g, "").length
                  ? ".5"
                  : "1",
              }}
              onClick={onCreateCollection}
              disabled={
                !newCollectionName.replace(/\s/g, "").length ||
                isSubmittingCreateCollection
              }
              className="text-[12px] rounded-[15px] cursor-pointer py-[8px] px-[18px] ml-[15px] border-solid border-[1px] border-black"
            >
              {isSubmittingCreateCollection ? (
                <Box className="flex justify-center">
                  <CircularProgress size="1rem" className="w-1 h-1" />
                </Box>
              ) : (
                <span>{strings.createCollection.cta}</span>
              )}
            </button>
          </div>
          <div className="text-[14px]">{strings.collection.label}</div>
          <div className="space-y-5 max-h-[225px] overflow-y-scroll scrollbar-w-0">
            <FormGroup className="overflow-y-scroll">
              {collections?.map((collection) => (
                <FormControlLabel
                  sx={{
                    svg: { fontSize: "20px" },
                    span: { fontSize: "14px" },
                  }}
                  key={collection.id}
                  control={
                    <Checkbox
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onCheckChange(collection, e.target.checked);
                      }}
                      id={collection.id.toString()}
                    />
                  }
                  label={collection.name}
                />
              ))}
            </FormGroup>
          </div>

          <div className="text-[14px]">
            {strings.collection.info.replace(
              "{{count}}",
              selectedCollection.length.toString()
            )}
          </div>

          <div className="flex w-full items-center justify-between">
            <Button
              className="p-2 disabled:bg-gray-200 bg-[#DC2227] hover:bg-[#DC2227] text-white rounded-none capitalize"
              type="submit"
              fullWidth
              disabled={
                selectedCollection.length === 0 ||
                isSubmittingCreateCollection ||
                isPending
              }
              onClick={onAddAssetsToCollections}
            >
              {strings.cta}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type LocalSchema = {
  title: string;
  createCollection: {
    label: string;
    placeholder: string;
    cta: string;
    notification: {
      success: string;
      failed: string;
    };
  };
  collection: {
    label: string;
    info: string;
  };
  notification: {
    success: string;
    failed: string;
  };
  cta: string;
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    title: "Add to collection",
    createCollection: {
      label: "Create new collection",
      placeholder: "Enter collection name",
      cta: "Create",
      notification: {
        success: "Created new Collection.",
        failed: "Failed to create new collection.",
      },
    },
    collection: {
      label: "Collections",
      info: "{{count}} Collections selected",
    },
    notification: {
      success: "Added assets to collections.",
      failed: "Failed add assets to collections.",
    },
    cta: "Add Asset to Collections",
  },
};
